import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Pencil, X } from 'lucide-react';
import { api } from '../api';
import { toast } from 'sonner';

// ---- Pure helpers ----
export function getEffectiveSpan(project, code) {
  const dayCount = Math.max(1, project?.dayCount || 1);
  const entry = project?.daySpans?.[code];
  if (entry && entry.fromDay && entry.toDay) {
    return { fromDay: entry.fromDay, toDay: entry.toDay, isDefault: false };
  }
  return { fromDay: 1, toDay: dayCount, isDefault: true };
}

export function spanCoversDay(span, day) {
  if (day == null) return true; // null = "All days"
  return day >= span.fromDay && day <= span.toDay;
}

export function spanLabel(span, dayCount) {
  if (span.isDefault) {
    return dayCount > 1 ? 'All days' : 'Day 1';
  }
  if (span.fromDay === span.toDay) return `Day ${span.fromDay}`;
  return `Day ${span.fromDay}–${span.toDay}`;
}


// ---- Day filter pill row (shown above tabs when project has multiple days) ----
// Accepts EITHER `dayNumbers` (preferred — a filtered list of visible day
// numbers) OR the legacy `dayCount` (renders Day 1..N). `dayNumbers` wins so
// wrapped days from PM don't appear as pills.
export function DayFilterPills({ dayNumbers, dayCount, selectedDay, onSelect }) {
  const nums =
    Array.isArray(dayNumbers) && dayNumbers.length
      ? [...new Set(dayNumbers)].sort((a, b) => a - b)
      : dayCount && dayCount > 0
        ? Array.from({ length: dayCount }, (_, i) => i + 1)
        : [];
  if (nums.length <= 1) return null;
  return (
    <div
      className="flex items-center gap-2 mb-3 flex-wrap"
      data-testid="day-filter-pills"
    >
      <Calendar size={13} className="text-amber-400" />
      <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">
        Show gear for
      </span>
      <button
        onClick={() => onSelect(null)}
        data-testid="day-filter-all"
        className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
          selectedDay == null
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
            : 'border-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-900'
        }`}
      >
        All days
      </button>
      {nums.map((d) => (
        <button
          key={d}
          onClick={() => onSelect(d)}
          data-testid={`day-filter-${d}`}
          className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
            selectedDay === d
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
              : 'border-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-900'
          }`}
        >
          Day {d}
        </button>
      ))}
    </div>
  );
}


// ---- Span editor popover: portal-rendered so it escapes overflow-hidden ancestors ----
function SpanEditor({ anchorEl, projectId, code, dayCount, current, onClose, onSaved }) {
  const [from, setFrom] = useState(current.fromDay);
  const [to, setTo] = useState(current.toDay);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  // Position the popover next to the anchor button
  useLayoutEffect(() => {
    if (!anchorEl) return;
    const updatePos = () => {
      const r = anchorEl.getBoundingClientRect();
      const W = 240;
      const H = 130; // approximate popover height
      const left = Math.max(8, Math.min(window.innerWidth - W - 8, r.right - W));
      // Prefer below the anchor; flip above if it would overflow the viewport
      const spaceBelow = window.innerHeight - r.bottom;
      const top =
        spaceBelow >= H + 8
          ? r.bottom + 6
          : Math.max(8, r.top - H - 6);
      setPos({ top, left });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [anchorEl, onClose]);

  const save = async () => {
    const f = Math.max(1, Math.min(dayCount, parseInt(from, 10) || 1));
    const t = Math.max(f, Math.min(dayCount, parseInt(to, 10) || f));
    setBusy(true);
    try {
      const updated = await api.setGearDaySpan(projectId, code, {
        fromDay: f,
        toDay: t,
      });
      onSaved(updated);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Could not save day span');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      ref={ref}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, width: 240 }}
      className="surface rounded-md shadow-2xl border border-neutral-800 p-3"
      onClick={(e) => e.stopPropagation()}
      data-testid={`span-editor-${code}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] tracking-wider uppercase text-neutral-500">
          Day range
        </span>
        <button
          onClick={onClose}
          className="text-neutral-500 hover:text-white"
          aria-label="Close"
        >
          <X size={12} />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          max={dayCount}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="w-16 bg-neutral-950 border border-neutral-800 text-white rounded px-2 py-1 text-xs"
          data-testid={`span-from-${code}`}
        />
        <span className="text-neutral-500 text-xs">to</span>
        <input
          type="number"
          min={1}
          max={dayCount}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="w-16 bg-neutral-950 border border-neutral-800 text-white rounded px-2 py-1 text-xs"
          data-testid={`span-to-${code}`}
        />
        <span className="text-[10px] text-neutral-500">of {dayCount}</span>
      </div>
      <div className="flex items-center justify-between mt-3">
        <button
          onClick={() => {
            setFrom(1);
            setTo(dayCount);
          }}
          className="text-[10px] text-neutral-500 hover:text-white underline"
        >
          Reset to all days
        </button>
        <button
          onClick={save}
          disabled={busy}
          className="btn-amber text-[11px] px-3 py-1 rounded disabled:opacity-60"
          data-testid={`span-save-${code}`}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>,
    document.body,
  );
}


export function DaySpanBadge({ project, code, onProjectUpdate }) {
  const [editing, setEditing] = useState(false);
  const btnRef = useRef(null);
  const dayCount = Math.max(1, project?.dayCount || 1);
  const span = getEffectiveSpan(project, code);

  if (dayCount <= 1) return null;

  const tone = span.isDefault
    ? 'text-neutral-400 border-neutral-800'
    : 'text-amber-300 border-amber-500/30 bg-amber-500/10';

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          setEditing((v) => !v);
        }}
        className={`text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded border inline-flex items-center gap-1 hover:bg-neutral-900 ${tone}`}
        title="Click to set day range"
        data-testid={`span-badge-${code}`}
      >
        <Calendar size={10} />
        {spanLabel(span, dayCount)}
        <Pencil size={9} className="opacity-60" />
      </button>
      {editing && (
        <SpanEditor
          anchorEl={btnRef.current}
          projectId={project.id}
          code={code}
          dayCount={dayCount}
          current={span}
          onClose={() => setEditing(false)}
          onSaved={(updated) => onProjectUpdate && onProjectUpdate(updated)}
        />
      )}
    </>
  );
}
